import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AsyncAdminService {
  
  private baseUrl = environment.BASE_LA;
  private bdvApiUrl = environment.BASE_BDV_API;
  
  // Estado reactivo del dashboard
  private dashboardData$ = new BehaviorSubject<any>(null);
  private systemStatus$ = new BehaviorSubject<any>(null);
  private loading$ = new BehaviorSubject<boolean>(false);
  private bdvTransactionIndicators$ = new BehaviorSubject<any>(null);
  
  constructor(private http: HttpClient) {}

  // ========================================
  // GETTERS PARA ESTADO REACTIVO
  // ========================================
  
  get dashboardData(): Observable<any> {
    return this.dashboardData$.asObservable();
  }
  
  get systemStatus(): Observable<any> {
    return this.systemStatus$.asObservable();
  }
  
  get loading(): Observable<boolean> {
    return this.loading$.asObservable();
  }

  get bdvTransactionIndicators(): Observable<any> {
    return this.bdvTransactionIndicators$.asObservable();
  }

  // ========================================
  // CONFIGURACIÓN ASYNC
  // ========================================

  /**
   * Obtener configuración async actual
   */
  async getAsyncConfig(): Promise<any> {
    try {
      this.loading$.next(true);
      
      const response = await this.http.get<any>(`${this.baseUrl}/api/admin/async-config`).toPromise();
      
      this.loading$.next(false);
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error obteniendo configuración:', error);
      throw error;
    }
  }

  /**
   * Actualizar configuración async
   */
  async updateAsyncConfig(configData: any): Promise<any> {
    try {
      this.loading$.next(true);
      
      const response = await this.http.put<any>(`${this.baseUrl}/api/admin/async-config`, configData).toPromise();
      
      this.loading$.next(false);
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error actualizando configuración:', error);
      throw error;
    }
  }

  /**
   * Toggle rápido de características
   */
  async toggleAsyncFeature(feature: string, enabled: boolean): Promise<any> {
    try {
      this.loading$.next(true);
      
      const response = await this.http.post<any>(
        `${this.baseUrl}/api/admin/async-config/toggle/${feature}`, 
        { enabled }
      ).toPromise();
      
      this.loading$.next(false);
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error en toggle:', error);
      throw error;
    }
  }

  // ========================================
  // CONFIGURACIÓN BDV
  // ========================================

  /**
   * Obtener configuración de BDV
   */
  async getBDVConfiguration(): Promise<any> {
    try {
      this.loading$.next(true);
      
      const response = await this.http.get<any>(`${this.baseUrl}/api/bdv-configuration`).toPromise();
      
      this.loading$.next(false);
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error obteniendo configuración BDV:', error);
      throw error;
    }
  }

  /**
   * Actualizar configuración de BDV
   */
  async updateBDVConfiguration(configData: any): Promise<any> {
    try {
      this.loading$.next(true);
      
      const response = await this.http.put<any>(`${this.baseUrl}/api/bdv-configuration`, configData).toPromise();
      
      this.loading$.next(false);
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error actualizando configuración BDV:', error);
      throw error;
    }
  }

  /**
   * Toggle auto-conciliación de BDV
   */
  async toggleBDVAutoConciliation(enabled: boolean): Promise<any> {
    try {
      this.loading$.next(true);
      
      const response = await this.http.post<any>(
        `${this.baseUrl}/api/bdv-configuration/toggle`, 
        { enabled }
      ).toPromise();
      
      this.loading$.next(false);
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error en toggle BDV:', error);
      throw error;
    }
  }

  /**
   * Verificar estado de auto-conciliación BDV
   */
  async getBDVAutoConciliationStatus(): Promise<any> {
    try {
      this.loading$.next(true);
      
      const response = await this.http.get<any>(`${this.baseUrl}/api/bdv-configuration/status`).toPromise();
      
      this.loading$.next(false);
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error verificando estado BDV:', error);
      throw error;
    }
  }

  // ========================================
  // ESTADO DEL SISTEMA
  // ========================================

  /**
   * Obtener estado del sistema
   */
  async getSystemStatus(): Promise<any> {
    try {
      this.loading$.next(true);
      
      const response = await this.http.get<any>(`${this.baseUrl}/api/admin/system-status`).toPromise();
      
      // Actualizar estado reactivo
      this.systemStatus$.next(response.data);
      
      this.loading$.next(false);
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error obteniendo estado del sistema:', error);
      throw error;
    }
  }

  /**
   * Obtener estado de colas RabbitMQ
   */
  async getQueueStatus(): Promise<any> {
    try {
      const response = await this.http.get<any>(`${this.baseUrl}/api/admin/queue-status`).toPromise();
      return response;
      
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error obteniendo estado de colas:', error);
      throw error;
    }
  }

  // ========================================
  // DASHBOARD Y MÉTRICAS
  // ========================================

  /**
   * Obtener overview del dashboard
   */
  async getDashboardOverview(filters: any = {}): Promise<any> {
    try {
      this.loading$.next(true);
      
      let params = new HttpParams();
      
      // Agregar filtros como query params
      if (filters.startDate) params = params.set('startDate', filters.startDate);
      if (filters.endDate) params = params.set('endDate', filters.endDate);
      if (filters.account_payment) params = params.set('account_payment', filters.account_payment);
      
      const response = await this.http.get<any>(
        `${this.baseUrl}/api/admin/dashboard/overview`, 
        { params }
      ).toPromise();
      
      // Actualizar estado reactivo
      this.dashboardData$.next(response.data);
      this.loading$.next(false);
      
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error obteniendo dashboard overview:', error);
      throw error;
    }
  }

  /**
   * Obtener análisis de reintentos
   */
  async getRetryAnalysis(filters: any = {}): Promise<any> {
    try {
      let params = new HttpParams();
      
      if (filters.startDate) params = params.set('startDate', filters.startDate);
      if (filters.endDate) params = params.set('endDate', filters.endDate);
      if (filters.account_payment) params = params.set('account_payment', filters.account_payment);
      
      const response = await this.http.get<any>(
        `${this.baseUrl}/api/admin/dashboard/metrics/retry-analysis`, 
        { params }
      ).toPromise();
      
      return response;
      
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error obteniendo análisis de reintentos:', error);
      throw error;
    }
  }

  /**
   * Obtener métricas de performance
   */
  async getPerformanceMetrics(filters: any = {}): Promise<any> {
    try {
      let params = new HttpParams();
      
      if (filters.startDate) params = params.set('startDate', filters.startDate);
      if (filters.endDate) params = params.set('endDate', filters.endDate);
      if (filters.account_payment) params = params.set('account_payment', filters.account_payment);
      
      const response = await this.http.get<any>(
        `${this.baseUrl}/api/admin/dashboard/metrics/performance`, 
        { params }
      ).toPromise();
      
      return response;
      
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error obteniendo métricas de performance:', error);
      throw error;
    }
  }

  // ========================================
  // INDICADORES BDV BOTÓN DE PAGOS
  // ========================================

  /**
   * Obtener indicadores de transacciones BDV del botón de pagos
   */
  async getBDVTransactionIndicators(filters: any = {}): Promise<any> {
    try {
      this.loading$.next(true);
      
      let params = new HttpParams();
      
      // Agregar filtros como query params (si se necesitan en el futuro)
      if (filters.startDate) params = params.set('startDate', filters.startDate);
      if (filters.endDate) params = params.set('endDate', filters.endDate);
      
      const response = await this.http.get<any>(
        `${this.bdvApiUrl}/api/transaction/indicators`, 
        { params }
      ).toPromise();
      
      // Actualizar estado reactivo BDV
      this.bdvTransactionIndicators$.next(response.data);
      this.loading$.next(false);
      
      return response;
      
    } catch (error) {
      this.loading$.next(false);
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error obteniendo indicadores BDV:', error);
      throw error;
    }
  }

  // ========================================
  // PERFIL BANCARIO (switch de contratos, separación 1-nov-2026)
  // ========================================

  /**
   * Perfil bancario activo e historial (solo consulta: el paso a CT ya se hizo y no hay vuelta)
   */
  async getPerfilBancario(): Promise<any> {
    return this.http.get<any>(`${this.baseUrl}/api/perfil-bancario`).toPromise();
  }

  // ========================================
  // UTILITIES
  // ========================================

  /**
   * Refrescar todos los datos del dashboard
   */
  async refreshDashboard(filters: any = {}): Promise<void> {
    try {
      await Promise.all([
        this.getDashboardOverview(filters),
        this.getSystemStatus(),
        this.getBDVTransactionIndicators(filters)
      ]);
    } catch (error) {
      console.error('❌ [ASYNC-ADMIN-SERVICE] Error refrescando dashboard:', error);
      throw error;
    }
  }

  /**
   * Limpiar estado
   */
  clearState(): void {
    this.dashboardData$.next(null);
    this.systemStatus$.next(null);
    this.bdvTransactionIndicators$.next(null);
    this.loading$.next(false);
  }
}
